import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { ApiException } from '@/lib/api/types';
import type { NotificationFeed } from './types';

/**
 * 알림 조회. (2026-08-19)
 *
 * 🔴 폴링 주기를 짧게 잡지 않는다
 *   알림의 원천이 배치다. 추천은 하루 한 번(16:30), 모의투자는 하루 세 번,
 *   배치 실패는 드물다. 30초마다 물어봐야 같은 답이 온다.
 *
 *   대신 응답이 가볍지 않다 - 5개 표를 훑고 종목명 지도를 만든다.
 *   5분이면 놓치는 느낌 없이 부하가 없다.
 */
const POLL_MS = 5 * 60 * 1000;

export function useNotifications(enabled: boolean) {
  return useQuery<NotificationFeed, ApiException>({
    queryKey: ['notifications'],
    queryFn: () => api.get<NotificationFeed>('/notifications'),
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
    // 탭이 뒤에 있을 때는 멈춘다. 안 보는 동안 부를 이유가 없다.
    refetchIntervalInBackground: false,
    // 🔴 로그인 전에는 부르지 않는다. 401 이 콘솔에 쌓인다
    enabled,
    retry: false,
  });
}

/**
 * 지금 이 화면의 버전을 서버에 알린다. (2026-08-30, docs/impl/95)
 *
 * 처음 보는 값이면 서버가 기록하고, 그것이 "새 버전이 배포되었습니다"
 * 알림의 원본이 된다. 백엔드는 화면 버전을 모르기 때문에 화면이 알려준다.
 *
 * 🔴 실패해도 조용히 넘어간다
 *   알림 하나가 안 뜰 뿐이고 앱은 멀쩡하다. 여기서 오류를 띄우면
 *   앱을 열 때마다 관계없는 경고를 보게 된다.
 */
export async function reportAppVersion(version: string): Promise<void> {
  try {
    await api.post<boolean>('/notifications/release', { version });
  } catch {
    // 무시
  }
}

export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation<void, ApiException>({
    mutationFn: () => api.post<void>('/notifications/read'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
